/**
 * TikTok 资源代理规则
 *
 * 展示、复制、下载、复制链接必须共用同一套规则，否则会出现「页面上能看，
 * 复制/保存出去的却是打不开的裸地址」。
 */

export const tiktokProxyUrl = 'https://prq-api.leyanbot.com/oversea_locust_proxy/image_proxy';

export interface TiktokProxyRule {
  /** TikTok CDN 是否可直连（可直连时不需要代理） */
  directAccessible: boolean;
  /** 当前访问地区 */
  countryCode?: string | null;
  /** 需要代理的 origin 列表，子串匹配 */
  originList?: string[];
}

const getHttpOrigin = (src: string) => {
  try {
    const { origin, protocol } = new URL(src);
    if (protocol !== 'http:' && protocol !== 'https:') {
      return null;
    }
    return origin;
  } catch (_) {
    return null;
  }
};

/** 原始资源地址 => 代理地址 */
export const getTiktokProxyImageSrc = (imageSrc: string) =>
  `${tiktokProxyUrl}?image_url=${encodeURIComponent(imageSrc)}`;

/** 是否已经是代理地址 */
export const isTiktokProxySrc = (src: string) => src.startsWith(tiktokProxyUrl);

/** 代理地址 => 原始资源地址（非代理地址原样返回） */
export const getProxyOriginSrc = (proxySrc: string) => {
  if (!isTiktokProxySrc(proxySrc)) {
    return proxySrc;
  }

  const [, src = ''] = proxySrc.split('?image_url=');

  try {
    return decodeURIComponent(src);
  } catch (_) {
    return src;
  }
};

/** 已经是代理地址时解包成原地址，否则原样返回 */
export const unwrapTiktokProxySrc = (src: string) =>
  isTiktokProxySrc(src) ? getProxyOriginSrc(src) : src;

/**
 * 该资源是否需要走代理
 *
 * 注意：入参应是原始资源地址；代理地址的 origin 不会命中列表，需先解包。
 */
export const isTiktokProxyEnabled = (src: string, rule: TiktokProxyRule) => {
  if (!src || rule.directAccessible || rule.countryCode !== 'CN') {
    return false;
  }

  const origin = getHttpOrigin(src);

  if (!origin || isTiktokProxySrc(src)) {
    return false;
  }

  return rule.originList?.some((item) => origin.includes(item)) ?? false;
};

const isOriginInProxyList = (originSrc: string, rule: TiktokProxyRule) => {
  const origin = getHttpOrigin(originSrc);
  return Boolean(origin && rule.originList?.some((item) => origin.includes(item)));
};

/**
 * 按代理规则解析最终使用的资源地址
 *
 * - 需要代理：返回代理地址（已经是代理地址则保持不变）
 * - 不需要代理：返回原始地址（已经包了代理则解包）
 * - 无法识别或与规则无关的地址：原样返回
 */
export const resolveTiktokProxySrc = (src: string, rule: TiktokProxyRule) => {
  if (!src) {
    return src;
  }

  const proxied = isTiktokProxySrc(src);
  const originSrc = proxied ? getProxyOriginSrc(src) : src;

  if (!getHttpOrigin(originSrc)) {
    return src;
  }

  if (isTiktokProxyEnabled(originSrc, rule)) {
    return proxied ? src : getTiktokProxyImageSrc(originSrc);
  }

  // 只有确认是按同一套规则包出来的代理地址才解包，避免误伤其它代理链接
  return proxied && isOriginInProxyList(originSrc, rule) ? originSrc : src;
};
